/**
 * How the forms screens show a submission (forms cards 10–11), shared by the submission page,
 * the inbox, My forms and the dashboard's "Waiting on you": a stage in the form's words, a part
 * status's colour, and whether a form is waiting on the viewer.
 */
import type { FormCapability, FormDefinition, FormItemStatus } from '@aiga/app-config/forms';
import type { MyFormRow } from '@/api/extensions/forms/formsApi';

/** A stage's label: the part's stages when `part` is given, else the form's. */
export function formStageLabel(def: FormDefinition, key: string | null, part?: string | null): string | null {
  if (!key) return null;
  const stages = part ? (def.parts.find((p) => p.key === part)?.stages ?? []) : def.stages;
  return stages.find((s) => s.key === key)?.label ?? key;
}

const ITEM_TONE: Record<FormItemStatus, string> = {
  OPEN: 'bg-gray-100 text-gray-700',
  SENT: 'bg-blue-100 text-blue-800',
  IN_REVIEW: 'bg-amber-100 text-amber-800',
  DONE: 'bg-emerald-100 text-emerald-800',
  CANCELLED: 'bg-slate-200 text-slate-600 line-through',
};

export const formItemTone = (status: FormItemStatus) => ITEM_TONE[status] ?? ITEM_TONE.OPEN;

/** The type's whole queue is the caller's to see (the inbox's rule: PROCESS or VIEW_ALL). */
export const seesFormQueue = (caps: readonly FormCapability[]) => caps.includes('PROCESS') || caps.includes('VIEW_ALL');

/**
 * Waiting on the viewer: someone asked them to fill it in, or it came back to them to fix. The
 * API's `waiting_on_me` also counts the viewer's own unsent drafts; those stay in My forms
 * without nagging on the dashboard.
 */
export const isWaitingOnYou = (row: MyFormRow, meId: string | null | undefined): boolean =>
  row.waiting_on_me && (row.status === 'RETURNED' || (!!meId && row.requested_of?.id === meId));
